/*
 * IMPORTANT: ID Requirements
 * - Every div element in this file MUST have an ID attribute
 * - IDs should be placed immediately after the opening div tag
 * - IDs should follow the 'team-selector-*' naming pattern
 * - When adding new divs, ensure they have unique, descriptive IDs
 * - DO NOT remove existing IDs during modifications
 *
 * Presentation:
 * - Defaults to the existing wrapped, internally scrollable team list
 * - Supports an opt-in vertical, non-scrollable list for narrow metadata panels
 */

import React from 'react';
import { useTeams } from '../../context/TeamContext';
import { useAllTeams } from '../../hooks/useAllTeams';
import { THEME_CLASSES } from '../../constants/themeConstants';

interface TeamSelectorProps {
  selectedTeamIds: any; //getter/setter for team IDs, function(value?: string): void | string
  isEditable?: boolean;
  useAllTeamsForSelection?: boolean; // If true, shows all teams instead of only user's teams
  layout?: 'wrap' | 'vertical';
}

const TeamSelector: React.FC<TeamSelectorProps> = ({
  selectedTeamIds,
  isEditable = true,
  useAllTeamsForSelection = false,
  layout = 'wrap',
}) => {
  const selectedTeamIdsArray: Number[] = selectedTeamIds() ? selectedTeamIds().split(',').map(Number) : [];
  const { teams: userTeams } = useTeams();
  const { allTeams: rawAllTeams, isLoading: loadingAllTeams } = useAllTeams();

  // Convert SimpleTeam to Team interface (TeamSelector expects userId/email for compatibility)
  const allTeams = rawAllTeams.map(t => ({
    teamId: t.teamId,
    teamName: t.teamName,
    userId: 0,  // Not needed for display
    email: ''   // Not needed for display
  }));

  // Determine which teams to show based on prop
  const availableTeams = useAllTeamsForSelection ? allTeams : userTeams;
  const possibleNoteTeams = (isEditable)
    ? availableTeams
    : availableTeams.filter(team => selectedTeamIdsArray.includes(team.teamId));
  const isVertical = layout === 'vertical';

  const handleTeamSelection = (target: HTMLInputElement, teamId: number) => {
    if (!isEditable) return;

    let newTeamIds: string;
    if (target.checked) {
      const newTeamIdArray = [...new Set([...(selectedTeamIdsArray || []), teamId])];
      newTeamIds = newTeamIdArray.join(',');
    } else {
      const newTeamIdArray = selectedTeamIdsArray.filter(id => id !== teamId);
      newTeamIds = newTeamIdArray.join(',');
    }
    selectedTeamIds(newTeamIds);
  };

  // Show loading state when fetching all teams
  if (useAllTeamsForSelection && loadingAllTeams) {
    return (
      <div
        id="team-selector-container"
        className={isVertical ? 'overflow-visible' : 'h-[100px] overflow-y-auto overflow-x-hidden'}
      >
        <div id="team-selector-loading" className={`flex items-center justify-center ${isVertical ? 'py-4' : 'h-full'}`}>
          <span className={`text-xs ${THEME_CLASSES.text.secondary}`}>Loading teams...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      id="team-selector-container"
      className={isVertical ? 'overflow-visible' : 'h-[100px] overflow-y-auto overflow-x-hidden'}
    >
      <div
        id="team-selector-list"
        className={isVertical
          ? 'flex w-full flex-col items-start gap-2 px-1 py-0.5'
          : 'flex w-full flex-wrap content-start gap-1.5 px-1 py-0.5'
        }
      >
        {possibleNoteTeams.map(team => {
          const isSelected = selectedTeamIdsArray?.includes(team.teamId);

          return (
            <label
              key={team.teamId}
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs ${isVertical ? 'w-fit max-w-full whitespace-normal' : ''} ${!isEditable ? 'cursor-not-allowed' : 'cursor-pointer'
                } ${isSelected
                  ? THEME_CLASSES.button.primary
                  : `${THEME_CLASSES.text.secondary} ${THEME_CLASSES.border} border ${isEditable ? 'hover:bg-opacity-10 hover:bg-primary' : ''
                  }`
                }`}
            >
              <input
                type="checkbox"
                className="hidden"
                checked={isSelected}
                disabled={!isEditable}
                onChange={(e) => handleTeamSelection(e.target, team.teamId)}
              />
              {team.teamName}
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default TeamSelector;
